import { createAsyncThunk } from "@reduxjs/toolkit";

import { apolloMutation, apolloQuery } from "Services/ApolloClientService";
import { SavedView } from "Types/SavedViews";

import { createSavedViewMutation, deleteSavedViewMutation, updateSavedViewMutation } from "./mutations";
import { getSavedViewsQuery } from "./queries";
import { addView, deleteView, setLoading, setSavedViews, updateView } from "./SavedViewsProvider";

export const loadSavedViews = createAsyncThunk<void, string>(
    "savedViews/loadSavedViews",
    async (page, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const { data } = await apolloQuery({
                fetchPolicy: "network-only",
                query: getSavedViewsQuery,
                variables: {
                    savedViewsQueryInput: { page },
                },
            });
            const savedViews = data?.getAllSavedViewsForPage?.items || [];
            dispatch(setSavedViews({ page, savedViews }));
            return savedViews;
            // update type to unknown and use normalizeError
        } catch (err: any) {
            return rejectWithValue(err);
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const saveNewView = createAsyncThunk<void, Pick<SavedView, "page" | "name" | "pageQuery">>(
    "savedViews/saveNewView",
    async ({ page, name, pageQuery }, { dispatch, rejectWithValue }) => {
        dispatch(setLoading(true));
        try {
            const { data } = await apolloMutation({
                mutation: createSavedViewMutation,
                variables: {
                    createSavedViewInput: {
                        name,
                        page,
                        pageQuery,
                    },
                },
            });
            dispatch(addView({ page, view: data.createSavedView }));
            return data;
        } catch (err: any) {
            return rejectWithValue(err instanceof Error ? err.message : err.toString());
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const updateSavedView = createAsyncThunk<void, SavedView>(
    "savedViews/updateSavedView",
    async ({ id, page, name, pageQuery, isDefault }, { dispatch, rejectWithValue }) => {
        dispatch(setLoading(true));
        try {
            const { data } = await apolloMutation({
                mutation: updateSavedViewMutation,
                variables: {
                    updateSavedViewInput: {
                        id,
                        name,
                        page,
                        pageQuery,
                        isDefault,
                    },
                },
            });
            dispatch(updateView({ page, view: data.updateSavedView }));
            return data;
            // update type to unknown and use normalizeError
        } catch (err: any) {
            return rejectWithValue(err instanceof Error ? err.message : err.toString());
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const deleteSavedView = createAsyncThunk<void, { id: string; page: string }>(
    "savedViews/deleteSavedView",
    async ({ id, page }, { dispatch, rejectWithValue }) => {
        dispatch(setLoading(true));
        try {
            const { data } = await apolloMutation({
                mutation: deleteSavedViewMutation,
                variables: { id },
            });
            dispatch(deleteView({ page, id }));
            return data;
            // update type to unknown and use normalizeError
        } catch (err: any) {
            return rejectWithValue(err instanceof Error ? err.message : err.toString());
        } finally {
            dispatch(setLoading(false));
        }
    }
);
